import React from 'react';
import type { FC } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener';
import {
	STAR_BUTTON_SHORTCUT,
	UNIVERSAL_STAR_BUTTON_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import type { ShortcutEvent } from '@confluence/shortcuts/entry-points/ShortcutListener';
import { PageStar } from '@confluence/action-buttons/entry-points/pageStar';

import { FavouriteButtonQuery } from './FavouriteButtonQuery.graphql';
import type {
	FavouriteButtonQuery as FavouriteButtonQueryType,
	FavouriteButtonQueryVariables,
} from './__types__/FavouriteButtonQuery';

const getStarShortcut = (contentType?: string, contentMode?: string) => {
	if (contentType === 'page' || contentType === 'blogpost') {
		if (contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft') {
			return {
				deprecatedAccelerator: undefined,
				accelerator: UNIVERSAL_STAR_BUTTON_SHORTCUT,
			};
		} else if (contentMode === 'view') {
			return {
				deprecatedAccelerator: STAR_BUTTON_SHORTCUT,
				accelerator: UNIVERSAL_STAR_BUTTON_SHORTCUT,
			};
		} else {
			return {
				deprecatedAccelerator: undefined,
				accelerator: undefined,
			};
		}
	} else {
		// for all other content types, use both keyboard shortcuts
		return {
			deprecatedAccelerator: STAR_BUTTON_SHORTCUT,
			accelerator: UNIVERSAL_STAR_BUTTON_SHORTCUT,
		};
	}
};

export const getStarShortcutString = (contentType?: string, contentMode?: string) => {
	if (contentType === 'page' || contentType === 'blogpost') {
		if (
			contentMode === 'live' ||
			contentMode === 'edit' ||
			contentMode === 'draft' ||
			contentMode === 'view'
		) {
			return UNIVERSAL_STAR_BUTTON_SHORTCUT;
		} else {
			return null;
		}
	} else {
		return UNIVERSAL_STAR_BUTTON_SHORTCUT;
	}
};

type FavouriteContentShortcutListenerType = {
	contentId: string;
	contentTitle: string;
	contentMode?: string;
	contentType?: string;
	source?: string;
};

export const FavouriteContentShortcutListener: FC<FavouriteContentShortcutListenerType> = ({
	contentId,
	contentTitle,
	contentMode,
	contentType,
	source = 'FavouriteMoreActionsMenuItem',
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { accelerator, deprecatedAccelerator } = getStarShortcut(contentType, contentMode);

	const { data, loading, error } = useQuery<
		FavouriteButtonQueryType,
		FavouriteButtonQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		FavouriteButtonQuery,
		{
			variables: {
				contentId,
			},
			fetchPolicy: 'cache-first',
		},
	);

	if (loading || error || !accelerator) {
		return null;
	}

	const isStarred = Boolean(data?.singleContent?.metadata.currentuser?.favourited?.isFavourite);

	const handleShortcut = (toggleStar) => (e: KeyboardEvent | ShortcutEvent) => {
		toggleStar(e);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'pageStar',
				attributes: {
					triggeredByKeyboardShortcut: true,
					action: isStarred ? 'unstar' : 'star',
					isInEditor: contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft',
				},
				source,
			},
		}).fire();
	};

	return (
		<PageStar
			contentId={contentId}
			isStarred={isStarred}
			contentTitle={contentTitle}
			shouldShowFlags
		>
			{({ toggle: toggleStar }) => (
				<GeneralShortcutListener
					deprecatedAccelerator={deprecatedAccelerator}
					accelerator={accelerator}
					listener={handleShortcut(toggleStar)}
				/>
			)}
		</PageStar>
	);
};
